import React, { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import Modal from '../components/Modal'
import { LoginForm } from '../pages/Login'

interface RequireAdminProps {
  children: React.ReactNode
}

const RequireAdmin: React.FC<RequireAdminProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth()
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)
  const [showLogin, setShowLogin] = useState(false)

  useEffect(() => {
    checkAdminAccess()
  }, [user, isAuthenticated])

  const checkAdminAccess = async () => {
    console.log('🔍 RequireAdmin: Checking admin access', { isAuthenticated, user })
    
    if (!isAuthenticated || !user) {
      console.log('🔍 RequireAdmin: Not authenticated or no user')
      setIsAdmin(false)
      setLoading(false)
      return
    }

    // Check if user has admin role or is a super admin email
    const adminEmails = ['admin@date-shake.com', 'nrmokh@gmail.com']
    const isSuperAdmin = adminEmails.includes(user.email?.toLowerCase())
    const hasAdminRole = user.role === 'admin'
    
    console.log('🔍 RequireAdmin: Admin check', { 
      email: user.email, 
      role: user.role, 
      isSuperAdmin, 
      hasAdminRole 
    })
    
    if (hasAdminRole || isSuperAdmin) {
      console.log('✅ RequireAdmin: User is admin')
      setIsAdmin(true)
    } else {
      console.log('❌ RequireAdmin: User is not admin')
      setIsAdmin(false)
    }
    
    setLoading(false)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying admin access...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-600 text-xl mb-4">🔒 Authentication Required</div>
            <p className="text-gray-600 mb-4">You must be logged in to access the admin portal.</p>
            <button
              onClick={() => setShowLogin(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Login
            </button>
          </div>
        </div>
        <Modal isOpen={showLogin} onClose={() => setShowLogin(false)} title="Admin Login">
          <LoginForm onSuccess={() => setShowLogin(false)} />
        </Modal>
      </>
    )
  }

  if (isAdmin === false) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">🚫 Access Denied</div>
          <p className="text-gray-600 mb-4">
            You don't have permission to access the admin portal.
          </p>
          <div className="space-x-4">
            <button
              onClick={() => window.location.href = '/dashboard'}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Go to Dashboard
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

export default RequireAdmin
